/**
 * Stored documents — chart files, caregiver credentials, and the PDF workbench.
 *
 * Replaces `src/api/pdfApi.js` and the `PdfList` keyon envelope:
 * `AddNewPdf`, `GetAllNursePdfs`, `DownloadManageAccountPdfs`,
 * `RemoveManageAccountPdfs`, plus pacmny-be's `#pdf`, `#getGrabbedImage`,
 * `#getPreview` and the four extraction verbs.
 *
 * **This is where the legacy platform's worst defect lived.** `AddNewPdf` took a
 * directory name and a filename from the request body, sanitised the filename
 * with a regex that preserved the extension, and wrote the result inside the web
 * root — so uploading `shell.php` was remote code execution on the next GET. The
 * same tree was served as static files, so every stored document was readable by
 * anyone who could guess a path, in any organization, with no token and no audit
 * record.
 *
 * Nothing on a request here can influence where bytes land: the storage key is
 * generated, the disk is private and outside `public/`, the MIME type comes from
 * the bytes rather than the multipart header, and `downloadDocument` is the only
 * way bytes leave — through a controller that has already run the scope, the
 * audit type and the care-relationship check. **Never build a URL to a stored
 * file by hand.**
 *
 * Reading your own licence and insurance scans is `documents:read`; reading
 * another caregiver's is `caregiver_documents:read`, which in the seeded matrix
 * only an administrator holds. The subject decides which applied, not the
 * endpoint.
 *
 * Rasterising, merging an extraction and OCR need a real engine, and where none
 * is configured those three answer 503 — an honest "this environment cannot do
 * that", distinguishable from a bug.
 */

import oidc from '@/auth/oidcClient';
import api, { BASE_URL } from './client';

// --- chart documents -------------------------------------------------------

/** `?filter[type]=` and `?filter[is_latest]=true` for the current version. */
export function listPatientDocuments(patientId, params = {}) {
  return api.list(`/patients/${patientId}/documents`, params);
}

/** Multipart: one part called `file`, plus `type`, `is_latest` and `note`. */
export function uploadPatientDocument(patientId, formData, onProgress) {
  return api.upload(`/patients/${patientId}/documents`, formData, { onProgress });
}

// --- caregiver documents ---------------------------------------------------
//
// Five legacy multipart branches with five different names for the same file
// part collapse onto one part called `file` and one `type` field.

export function listUserDocuments(userId, params = {}) {
  return api.list(`/users/${userId}/documents`, params);
}

export function uploadUserDocument(userId, formData, onProgress) {
  return api.upload(`/users/${userId}/documents`, formData, { onProgress });
}

/**
 * One caregiver's documents as a single ZIP. An empty set is a 404, not a
 * zero-entry archive — `DownloadManageAccountPdfs` served a ZIP and a JSON error
 * from the same branch, both with HTTP 200 and the ZIP content type already
 * sent, so a client could not tell them apart. Bounded by count and by total
 * bytes: a bulk download over ePHI is an exfiltration primitive.
 */
export function downloadUserDocumentArchive(userId, params = {}) {
  return api.download(`/users/${userId}/documents/archive`, params);
}

// --- one document ----------------------------------------------------------

export function getDocument(documentId) {
  return api.get(`/documents/${documentId}`);
}

export function downloadDocument(documentId) {
  return api.download(`/documents/${documentId}/content`);
}

/**
 * Soft, and the object stays on the disk: purging bytes is a scheduled act
 * against a retention policy, not something a DELETE decides. A credential
 * document takes main's optional `reason` (DeleteCredentialDocument); it
 * travels in the body, because it is PHI-capable text and a URL is logged.
 */
export function deleteDocument(documentId, { reason } = {}) {
  return api.delete(`/documents/${documentId}`, reason === undefined ? {} : { data: { reason } });
}

// --- credential documents (main's ListCredentialDocuments, UploadNurseFile) --

/** `scope`: own | owner | org; `owner` names the person for `owner`. */
export function listCredentialDocuments(params = {}) {
  return api.list('/credential-documents', params);
}

/**
 * The immediate upload a form makes the moment a file is picked, ahead of the
 * create or edit that sends its uid. Multipart: `file`, `category` (license,
 * insurance, misc) and `subject_user_id` (the person, or an id that is
 * nobody's yet for a person the form is still creating).
 */
export function stageCredentialDocument(formData, onProgress) {
  return api.upload('/credential-documents', formData, { onProgress });
}

/** One person's credential documents as credentials.zip (main's bundle). */
export function downloadCredentialBundle(userId) {
  return api.download(`/users/${userId}/documents/archive`);
}

/**
 * A form releasing its own unsaved immediate upload (main's
 * DeleteCredentialDocument with `unclaimed_only`): the API removes it only
 * while it is still the caller's unclaimed upload, so a saved credential is
 * never touched this way, and the sweep collects whatever this misses.
 *
 * Sent with `fetch` and `keepalive`, not axios, so it finishes when the page is
 * going away and stays clear of the axios handlers that would send a user to
 * sign in again in the middle of a cancel. That also means the bearer token is
 * attached here. Best-effort: never throws, resolves whether the API reported
 * the file released.
 *
 * `deps` (`fetch`, `accessToken`) are injectable for tests.
 */
export async function discardUnclaimedUpload(fileUid, deps = {}) {
  if (typeof fileUid !== 'string' || !/^[a-f0-9]{32}$/.test(fileUid)) return false;

  const send = deps.fetch || (typeof fetch === 'function' ? (...args) => fetch(...args) : null);

  if (!send) return false;

  try {
    const token = await (deps.accessToken ? deps.accessToken() : oidc.getAccessToken());
    const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };

    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await send(`${BASE_URL}/api/v1/documents/${fileUid}`, {
      method: 'DELETE',
      keepalive: true,
      // The API is stateless and cross-origin; a cookie would be both useless
      // and a CSRF surface.
      credentials: 'omit',
      headers,
      body: JSON.stringify({ unclaimed_only: true }),
    });

    if (!res || !res.ok) return false;

    const body = await res.json();

    return !!body && !!body.data && body.data.file_uid === fileUid;
  } catch (_) {
    return false;
  }
}

// --- the PDF workbench -----------------------------------------------------

export function listPdfDocuments(params = {}) {
  return api.list('/pdf-documents', params);
}

/** Multipart: `patient_id` plus one part called `file`. */
export function uploadPdfDocument(formData, onProgress) {
  return api.upload('/pdf-documents', formData, { onProgress });
}

export function getPdfDocument(pdfId) {
  return api.get(`/pdf-documents/${pdfId}`);
}

export function downloadPdfDocument(pdfId) {
  return api.download(`/pdf-documents/${pdfId}/content`);
}

/** New — the legacy system had no PDF delete at all. */
export function deletePdfDocument(pdfId) {
  return api.delete(`/pdf-documents/${pdfId}`);
}

/**
 * Rasterise one rectangle of one page. A POST that creates nothing durable: a
 * rendering is still a thing, so it is a sub-resource rather than a verb.
 */
export function grabPdfRegion(pdfId, payload) {
  return api.post(`/pdf-documents/${pdfId}/grabs`, payload);
}

// --- extractions -----------------------------------------------------------

export function getExtraction(pdfId) {
  return api.get(`/pdf-documents/${pdfId}/extraction`);
}

/**
 * One endpoint for the four legacy verbs — add page, remove page, reorder,
 * rotate — applied as one transaction, so a half-applied edit cannot survive a
 * failure mid-sequence.
 */
export function updateExtraction(pdfId, operations) {
  return api.patch(`/pdf-documents/${pdfId}/extraction`, { operations });
}

export function discardExtraction(pdfId) {
  return api.delete(`/pdf-documents/${pdfId}/extraction`);
}

/**
 * Render the draft without saving it — hence `documents:read` and a blob back
 * rather than a resource. A preview that rewrote the operator's draft would be a
 * write behind a read scope.
 */
export function previewExtraction(pdfId, payload) {
  return api.post(`/pdf-documents/${pdfId}/extraction/preview`, payload, {
    responseType: 'blob',
  });
}

/** Text out of a selection. The legacy shelled out to a command line inline. */
export function runOcr(pdfId, payload) {
  return api.post(`/pdf-documents/${pdfId}/ocr`, payload);
}

export default {
  listPatientDocuments, uploadPatientDocument,
  listUserDocuments, uploadUserDocument, downloadUserDocumentArchive,
  listCredentialDocuments, stageCredentialDocument, downloadCredentialBundle, discardUnclaimedUpload,
  getDocument, downloadDocument, deleteDocument,
  listPdfDocuments, uploadPdfDocument, getPdfDocument, downloadPdfDocument,
  deletePdfDocument, grabPdfRegion,
  getExtraction, updateExtraction, discardExtraction, previewExtraction, runOcr,
};
